/**
 * Text Sanitizer Utilities
 *
 * Provides functions to sanitize text for chat presentation.
 * Per presentation contract: chat bubbles render only narration,
 * markdown/code content should be emitted as artifacts only.
 *
 * This sanitizer strips common markdown emphasis markers so that
 * any markdown that leaks into chat text renders as plain narration.
 */

/**
 * Sanitize text for chat bubble presentation.
 * Strips markdown emphasis markers (bold, italic) so text reads as plain narration.
 *
 * Does NOT render markdown - just removes the syntax markers.
 * Heavy markdown (fenced code, headings) is handled separately by the
 * containsDisallowedMarkdownForChat() check in chatApi.ts.
 *
 * @param text - Raw text that may contain markdown emphasis
 * @returns Plain text with emphasis markers removed
 */
export function sanitizeForChat(text: string): string {
    if (!text) return text;

    let result = text;

    // Remove bold markers: **text** or __text__
    // Use non-greedy match to handle multiple bold sections
    result = result.replace(/\*\*([^*]+)\*\*/g, '$1');
    result = result.replace(/__([^_]+)__/g, '$1');

    // Remove italic markers: *text* or _text_
    // Be careful not to match list items (- or * at line start)
    // Only match single asterisks/underscores that wrap text
    result = result.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '$1');
    result = result.replace(/(?<!_)_([^_\n]+)_(?!_)/g, '$1');

    // Remove inline code markers: `text`
    // Keep the text content, just remove the backticks
    result = result.replace(/`([^`\n]+)`/g, '$1');

    // Normalize list markers at line start to simple dashes
    // Convert * item to - item for consistency
    result = result.replace(/^(\s*)\* /gm, '$1- ');

    return result;
}

/**
 * Normalize escaped newlines in artifact content.
 * Handles double-encoded content where \n appears as literal \\n.
 *
 * Only unescapes when content looks double-encoded:
 * - Has \\n sequences but no actual newlines
 *
 * @param content - Raw content that may have escaped newlines
 * @returns Content with normalized newlines
 */
export function normalizeNewlines(content: string): string {
    if (!content) return content;

    // Check if content has \\n but no actual newlines
    const hasEscapedNewlines = content.includes('\\n');
    const hasRealNewlines = content.includes('\n');

    // Only unescape if it looks double-encoded
    if (hasEscapedNewlines && !hasRealNewlines) {
        return content.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '');
    }

    // Also handle \r\n -> \n normalization
    return content.replace(/\r\n/g, '\n');
}
